import { Button, Grid, Typography } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { IProductDisplayItem } from 'src/common/interfaces/dtos/IProductDisplayItem';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { AppState } from 'src/common/redux/configure-store';
import { ColumnCount } from './product-display-layout';
import { getQuantityDisplay } from './service/product-quantity-display';
import { styles } from './product-display-layout-styles';
import XLRLogger from 'src/common/helpers/logger';

interface PLUGridItemProps {
  product: IProductDisplayItem;
  columnCount: ColumnCount;
  handleSelectedProduct: (selectedProduct: IProductDisplayItem) => void;
  itemType: EJRecordTypes;
}

const PLUGridItem: React.FC<PLUGridItemProps> = ({
  product,
  columnCount,
  handleSelectedProduct,
  itemType,
}: PLUGridItemProps): JSX.Element => {
  const isLeftHanded = useSelector<AppState, boolean>(state => state.cashierPreferences.leftHanded);

  const classes = styles({ leftHanded: isLeftHanded });

  const itemList = useSelector<AppState, IElectronicJournalRecord[]>(
    state => state.itemList.ejItems,
  );

  const { t } = useTranslation();

  const unitsLabel = product.UnitOfMeasure
    ? (product.Quantity ?? 0) > 1
      ? t('UOMS_')
      : t(`UOM_${product.UnitOfMeasure}`)
    : '';

  const getGridSize = (columnCount: ColumnCount): ColumnCount => {
    return (12 / columnCount) as ColumnCount;
  };

  return (
    <Grid item xs={getGridSize(columnCount)} className={classes.pluGridItem}>
      <Button centerRipple>
        <Grid
          container
          data-testid='product-container'
          className={clsx(classes.productContainer, 'fullWidth', 'fullHeight')}
          onClick={() => handleSelectedProduct(product)}
        >
          <img
            className={classes.productImage}
            data-testid='product-image'
            src={
              product.EmeraldImageUrl
                ? `${product.EmeraldImageUrl}`
                : `data:image/png;base64,${product.Image}`
            }
            onError={({ currentTarget }) => {
              if (product.EmeraldImageUrl !== undefined) {
                const message = `PLU image failed to load: ${product.EmeraldImageUrl}`;
                XLRLogger.error({
                  message: message,
                  source: 'plu-grid.tsx',
                });
              }
              currentTarget.onerror = null; // prevents looping
              currentTarget.style.display = 'none';
            }}
            alt=''
          />
          <Grid
            container
            direction='column'
            data-testid='all-items-product-card'
            className={classes.productDetailParentContainer}
          >
            <Grid container direction='column' className={classes.productDetailContainer}>
              <Grid item className='fullWidth'>
                <Typography
                  variant='subtitle1'
                  className={clsx(classes.description, classes.overflowEllipsis)}
                  data-testid='all-items-product-name'
                >
                  {product.Name ?? ''}
                </Typography>
              </Grid>
              <Grid item className='fullWidth'>
                <Typography
                  data-testid='all-items-product-plu'
                  className={clsx('fullWidth', classes.price, classes.overflowEllipsis)}
                >
                  {product.Id}
                </Typography>
              </Grid>
              <Grid container>
                <Grid item xs={8}>
                  <Typography
                    className={clsx(classes.price, classes.overflowEllipsis)}
                    data-testid='all-items-product-price'
                  >
                    {`${convertCurrency(product.Price ?? 0)} | ${product.Quantity ??
                      ''} ${unitsLabel}`}
                  </Typography>
                </Grid>
                <Grid item xs={4} className={classes.quantityContainer}>
                  <Typography
                    className={clsx(classes.quantity, classes.overflowEllipsis)}
                    data-testid='all-items-quantity-tracker'
                  >
                    {getQuantityDisplay({
                      selectedItem: product,
                      uomText: t('UOM_'),
                      uomsText: t('UOMS_'),
                      itemList,
                      itemType,
                    })}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Button>
    </Grid>
  );
};

export default React.memo(PLUGridItem);
